/**
 * Columns, labels and colours for the List Management tables.
 *
 * Column set and order follow the design prototype (Inbox.dc.html `listVals`,
 * contact columns), mapped onto the canonical staged-value keys the API
 * returns in `row.values`. Colours are the prototype's exact hex values.
 */

const v = (key) => (row) => row.values?.[key] ?? '';

/**
 * One name per field inside List Management (#1014), matching the import field
 * catalog (`services/list_management/fields.py` `field_catalog`) except the three
 * kept "Company …" location labels. Read by the default columns, the catalog
 * columns, the row drawer and the Resolve / Enrich dialogs; never by the catalog
 * at runtime (a review-only user cannot read /fields). `lmColumns.labels.test.js`
 * and `test_list_management_contract.py` compare it with
 * `__tests__/catalogLabels.snapshot.json`, so a catalog label change fails a test
 * until both are updated.
 */
export const FIELD_LABELS = {
    // Company fields
    company_name: 'Company Name',
    industry: 'Industry',
    size: 'Company Size',
    annual_revenue: 'Annual Revenue',
    website: 'Website URL',
    company_phone: 'Company Phone',
    company_email: 'Company Email',
    address: 'Street Address',
    city: 'Company City',
    state_province: 'Company State/Province',
    country: 'Company Country',
    region: 'Region',
    company_hq_country: 'Company HQ Country',
    company_linkedin_url: 'Company LinkedIn URL',
    employee_approx: 'Employee Number Approx',
    investor: 'Lead Investor',
    investor_type: 'Investor Type',
    company_summary: 'Company Summary',
    // Contact fields
    first_name: 'First Name',
    last_name: 'Last Name',
    personal_email: 'Personal Email',
    phone: 'Personal Phone',
    mobile: 'Mobile/WhatsApp',
    title: 'Job Title',
    department: 'Department',
    seniority: 'Seniority',
    buying_role: 'Buying Role',
    linkedin_url: 'Personal LinkedIn URL',
    source_tag: 'Source Tag',
    status_event: 'Status Event',
    tags: 'Tags',
    // Relationship fields
    related_company_name: 'Related Company',
    relationship_type: 'Relationship Type',
    // List Management's own columns: the owner (the catalog's `assigned_to`) and the row's workflow state.
    owner: 'Owner',
    status: 'Import Status',
};

/** Kept on purpose (#1014): they differ from the catalog's "City", "State / Province" and "Country". */
export const CATALOG_LABEL_EXCEPTIONS = ['city', 'state_province', 'country'];

/** The label List Management shows for a field key: the map, then the catalog (custom fields), then the key. */
export const labelOf = (key, catalogLabels = {}) => FIELD_LABELS[key] || catalogLabels?.[key] || key;

/** The prototype's 20 contact columns, in order. Each carries its tooltip sentence (#975). */
export const CONTACT_COLUMNS = [
    { id: 'first_name', label: FIELD_LABELS.first_name, description: 'The contact\'s first name from the import; select it to open the row.', get: v('first_name'), sort: 'first_name', primary: true },
    { id: 'last_name', label: FIELD_LABELS.last_name, description: 'The contact\'s last name from the import.', get: v('last_name'), sort: 'last_name' },
    { id: 'personal_email', label: FIELD_LABELS.personal_email, description: 'The contact\'s email from the import; an exact email match links the row to an existing contact.', get: v('personal_email'), sort: 'email' },
    { id: 'mobile', label: FIELD_LABELS.mobile, description: 'The contact\'s mobile or WhatsApp number from the import.', get: v('mobile') },
    { id: 'title', label: FIELD_LABELS.title, description: 'The contact\'s job title from the import.', get: v('title') },
    { id: 'seniority', label: FIELD_LABELS.seniority, description: 'The contact\'s seniority level from the import.', get: v('seniority') },
    { id: 'linkedin_url', label: FIELD_LABELS.linkedin_url, description: 'The contact\'s LinkedIn profile URL from the import.', get: v('linkedin_url') },
    { id: 'source_tag', label: FIELD_LABELS.source_tag, description: 'The source tag from the import.', get: v('source_tag') },
    { id: 'tags', label: FIELD_LABELS.tags, description: 'Tags from the import.', get: v('tags') },
    { id: 'company_name', label: FIELD_LABELS.company_name, description: 'The company name from the import; a contact row gets a company only when this is filled.', get: v('company_name'), sort: 'company_name' },
    { id: 'company_email', label: FIELD_LABELS.company_email, description: 'The company\'s email from the import; a non-free-mail domain helps match the company.', get: v('company_email') },
    { id: 'company_phone', label: FIELD_LABELS.company_phone, description: 'The company\'s phone number from the import.', get: v('company_phone') },
    { id: 'city', label: FIELD_LABELS.city, description: 'The company\'s city from the import.', get: v('city') },
    { id: 'state_province', label: FIELD_LABELS.state_province, description: 'The company\'s state or province from the import.', get: v('state_province') },
    { id: 'country', label: FIELD_LABELS.country, description: 'The company\'s country from the import.', get: v('country') },
    { id: 'website', label: FIELD_LABELS.website, description: 'The company\'s website from the import; its domain is used to match an existing company.', get: v('website') },
    { id: 'industry', label: FIELD_LABELS.industry, description: 'The company\'s industry from the import.', get: v('industry') },
    { id: 'employee_approx', label: FIELD_LABELS.employee_approx, description: 'The company\'s size from the import: a number becomes the employee count and a range stays a range.', get: v('employee_approx') },
    { id: 'owner', label: FIELD_LABELS.owner, description: 'The owner this row is assigned to in For Pooling; Commit needs one.', get: (row) => row.owner_name || 'Unassigned', sort: 'owner_name' },
    { id: 'status', label: FIELD_LABELS.status, description: 'Where the row is on its way from Imports through Staging and For Pooling to the CRM.', get: null, kind: 'status', sort: 'state' },
];

/** List Management additions (shown by default where they matter). */
export const EXTRA_COLUMNS = [
    { id: 'entity', label: 'Entity', description: 'Whether the row becomes a Company or a Contact in the CRM.', get: (row) => row.entity_type || '—', kind: 'entity' },
    { id: 'match', label: 'Match', description: 'How the row was matched to an existing CRM record.', get: null, kind: 'match', sort: 'match_status' },
    { id: 'issues', label: 'Issues', description: 'Validation problems that stop the row from moving on until they are fixed.', get: null, kind: 'issues', sort: 'validation_status' },
];

/**
 * State columns lead, straight after the record name (#770 QA): a row's
 * Entity / Match / Issues / Import Status must be readable without scrolling sideways.
 * Import Status comes before the owner so it still fits at 1280px, where the shell
 * and Sources navs leave the table about 660px. The prototype's remaining
 * columns follow in their own order.
 */
const LEAD_COLUMNS = {
    // Entity / Match / Issues are offered on every tab (#974: "the state columns (Entity, Match,
    // Issues, Status, Owner)"). Where a tab did not show them before they come after the columns
    // it always led with, so the default order is unchanged and a user who switches them on
    // finds them beside Import Status, not at the far right.
    imports: ['first_name', 'last_name', 'status', 'entity', 'match', 'issues'],
    staging: ['first_name', 'last_name', 'entity', 'match', 'issues', 'status', 'owner'],
    pool: ['first_name', 'last_name', 'entity', 'match', 'status', 'owner', 'issues'],
};

/**
 * State columns a tab offers but does NOT show until the user switches them on:
 * the tabs keep the default view they always had (Imports: record, Import Status, Owner;
 * For Pooling: no Issues column). Every other state column is on by default.
 */
const OFFERED_OFF_BY_DEFAULT = {
    imports: new Set(['entity', 'match', 'issues']),
    staging: new Set(),
    pool: new Set(['issues']),
};

/**
 * The one-time Contact Owner migration (#862/#864): a view filtered to ONE
 * owner-migration import names the owner column "New Contact Owner" and adds
 * "Current Contact Owner" before it — never a raw id. Ordinary imports keep
 * "Owner" (a pre-commit pool assignment).
 */
const CURRENT_OWNER_COLUMN = {
    id: 'current_owner', label: 'Current Contact Owner',
    description: 'The contact\'s owner when the migration was staged.',
    get: (row) => {
        const m = row.migration || {};
        if (m.current_owner_label) return m.current_owner_label;
        return m.current_owner_id ? 'Former member' : 'No Contact Owner';
    },
};

// ── catalog-driven columns (#974) ─────────────────────────────────────────────

/** A column read from the file the row was imported from: `src:<header>`. */
export const SRC_PREFIX = 'src:';
export const srcId = (header) => `${SRC_PREFIX}${header}`;

/** Columns that describe the row's state, not a CRM field (picker group STATE). */
const STATE_IDS = new Set(['entity', 'match', 'issues', 'status', 'owner', 'current_owner']);

// Which entity each of today's 20 columns belongs to, for the picker's kind label
// when no catalog is loaded (a review-only user cannot read /fields).
const COMPANY_COLUMN_IDS = new Set([
    'company_name', 'company_email', 'company_phone', 'city', 'state_province', 'country', 'website',
    'industry', 'employee_approx',
]);

const KIND_BY_ENTITY = { account: 'Company', company: 'Company', contact: 'Contact', relationship: 'Relationship', migration: 'Contact' };

// Owner appears once, as the state column `owner`, never as a generated field.
const OWNER_KEY = 'assigned_to';

// Generic descriptions (#975 forbids a column without one).
const describeCatalogField = (kind) => `${kind} field mapped from the import.`;
const describeCustomField = (kind) => `Custom ${kind.toLowerCase()} field, created in Admin → Custom Fields or while importing.`;
const FILE_COLUMN_DESCRIPTION = 'Column from the imported file. Shown here only; never written to the CRM.';

/** The catalog's fields, flat and de-duplicated by key; built-ins first, custom after. */
function catalogFields(catalog) {
    if (!catalog) return [];
    const groups = [catalog.contact_fields, catalog.company_fields || catalog.account_fields, catalog.relationship_fields];
    const seen = new Set();
    const out = [];
    groups.forEach((list) => (list || []).forEach((f) => {
        if (!f?.key || f.key === OWNER_KEY || seen.has(f.key)) return;
        seen.add(f.key);
        out.push({ key: f.key, label: f.label || f.key, entity: f.entity, isCustom: Boolean(f.is_custom), fieldType: f.type });
    }));
    return [...out.filter((f) => !f.isCustom), ...out.filter((f) => f.isCustom)];
}

const LEGACY_IDS = CONTACT_COLUMNS.filter((c) => !STATE_IDS.has(c.id)).map((c) => c.id);

/**
 * The ids the imports in view carry: mapped catalog keys, and the file columns
 * that were selected but left unmapped (`src:<header>`). An import that reports
 * no mapping (or no imports at all) stands for today's default columns, so an
 * empty tab looks as it always did.
 */
function carriedIds(importsInView) {
    const ids = new Set();
    const list = (importsInView || []).filter(Boolean);
    if (!list.length) { LEGACY_IDS.forEach((id) => ids.add(id)); return ids; }
    list.forEach((imp) => {
        const cols = imp.mapping?.columns;
        const noHeaders = Array.isArray(imp.headers) && imp.headers.length === 0;
        // No mapping info (or a file with neither headers nor mapped columns) stands for today's defaults.
        if (!cols || typeof cols !== 'object' || (noHeaders && !Object.keys(cols).length)) { LEGACY_IDS.forEach((id) => ids.add(id)); return; }
        if (isLegacyCarryover(imp)) {
            // The staging carry-over (migration 2026_10_05_120000) stores no file headers:
            // its `columns` are already CRM keys, and each value names a CRM field it carried.
            Object.values(cols).forEach((key) => { if (key) ids.add(key); });
            return;
        }
        const headers = Array.isArray(imp.headers) ? imp.headers : Object.keys(cols);
        const chosen = Array.isArray(imp.mapping.selected_columns) ? new Set(imp.mapping.selected_columns) : null;
        headers.forEach((h) => {
            if (chosen && !chosen.has(h)) return;
            if (cols[h]) ids.add(cols[h]); else ids.add(srcId(h));
        });
    });
    return ids;
}

/** Empty `headers` with a populated `mapping.columns`: a legacy carry-over, not a file import. */
const isLegacyCarryover = (imp) => Array.isArray(imp?.headers) && imp.headers.length === 0
    && Object.keys(imp?.mapping?.columns || {}).length > 0;

/** File columns carried by the imports in view, in file order, de-duplicated. */
function fileHeaders(importsInView) {
    const out = [];
    const seen = new Set();
    (importsInView || []).filter(Boolean).forEach((imp) => {
        const cols = imp.mapping?.columns;
        if (!cols || typeof cols !== 'object' || isLegacyCarryover(imp)) return;
        const headers = Array.isArray(imp.headers) ? imp.headers : Object.keys(cols);
        const chosen = Array.isArray(imp.mapping.selected_columns) ? new Set(imp.mapping.selected_columns) : null;
        headers.forEach((h) => {
            if ((chosen && !chosen.has(h)) || cols[h] || seen.has(h)) return;
            seen.add(h);
            out.push(h);
        });
    });
    return out;
}

/**
 * Today's list, in today's order, plus (when given) every catalog field and the
 * file columns the imports in view carry. Every column also says which picker
 * group it belongs to (`group`), its kind label and whether it is on by default
 * (`auto`). With neither `catalog` nor `importsInView` this is exactly the
 * 20 / 23 / 22 columns it has always been.
 */
export function columnsFor(tab, { ownerMigration = false, catalog = null, importsInView = null } = {}) {
    // Entity, Match and Issues are options on every tab (#974); the tab decides which are on by default.
    const extras = EXTRA_COLUMNS;
    const offByDefault = OFFERED_OFF_BY_DEFAULT[tab] || new Set();
    let base = [...CONTACT_COLUMNS];
    if (ownerMigration) {
        base = base.flatMap((c) => (c.id === 'owner'
            // The rename changes what the column means (#864), so its sentence changes too (#975).
            ? [CURRENT_OWNER_COLUMN, { ...c, label: 'New Contact Owner', description: 'The owner this migration gives the contact when its part commits.' }]
            : [c]));
    }
    const all = [...base, ...extras];
    const lead = (LEAD_COLUMNS[tab] || ['first_name', 'last_name'])
        .flatMap((id) => (ownerMigration && id === 'owner' ? ['current_owner', 'owner'] : [id]));
    const byId = Object.fromEntries(all.map((c) => [c.id, c]));
    const ordered = [...lead.map((id) => byId[id]).filter(Boolean), ...all.filter((c) => !lead.includes(c.id))];

    const carried = carriedIds(importsInView);
    const fields = catalogFields(catalog);
    const entityOf = Object.fromEntries(fields.map((f) => [f.key, f.entity]));
    const decorate = (c) => {
        const state = STATE_IDS.has(c.id);
        // The lead columns (the name, state columns) are always on, so they count as part of the imports.
        const carriedHere = c.primary || lead.includes(c.id) || carried.has(c.id);
        const kind = state ? 'State'
            : KIND_BY_ENTITY[entityOf[c.id]] || (COMPANY_COLUMN_IDS.has(c.id) ? 'Company' : 'Contact');
        return {
            ...c, kindLabel: kind,
            group: state ? 'state' : carriedHere ? 'imports' : 'other',
            auto: !offByDefault.has(c.id) && (state || lead.includes(c.id) || carriedHere),
        };
    };
    const columns = ordered.map(decorate);

    const have = new Set(columns.map((c) => c.id));
    fields.forEach((f) => {
        if (have.has(f.key)) return;
        const kindLabel = KIND_BY_ENTITY[f.entity] || 'Contact';
        const isCarried = carried.has(f.key);
        columns.push({
            id: f.key, label: FIELD_LABELS[f.key] || f.label, get: v(f.key), isCustom: f.isCustom, fieldType: f.fieldType, kindLabel,
            description: f.isCustom ? describeCustomField(kindLabel) : describeCatalogField(kindLabel),
            group: isCarried ? 'imports' : 'other', auto: isCarried,
        });
    });
    fileHeaders(importsInView).forEach((header) => {
        columns.push({
            id: srcId(header), label: header, header, isFile: true, kindLabel: 'File',
            get: (row) => row.source?.[header] ?? '',
            description: FILE_COLUMN_DESCRIPTION, group: 'imports', auto: true,
        });
    });
    return columns;
}

// ── visibility (#974, D2, P4) ─────────────────────────────────────────────────
//
// visible(c) = c.primary || shown.has(c.id) || (!hidden.has(c.id) && autoDefault(c))
//
// `hidden` / `shown` are the user's overrides on top of the automatic default
// (imported columns on). Unknown ids are ignored: a deleted custom field or a
// column from another file simply never matches a column.

export const autoDefault = (c) => Boolean(c.auto);

export const isColumnVisible = (c, { hidden, shown }) =>
    Boolean(c.primary) || shown.has(c.id) || (!hidden.has(c.id) && autoDefault(c));

export const visibleColumns = (columns, prefs) => columns.filter((c) => isColumnVisible(c, prefs));

const storageKey = (kind, tab) => `lm.${kind}Columns.${tab}`;

function readSet(kind, tab) {
    try {
        const parsed = JSON.parse(window.localStorage.getItem(storageKey(kind, tab)) || '[]');
        return new Set(Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string') : []);
    } catch { return new Set(); }
}

/** Both override sets for a tab. Storage that is missing or malformed reads as empty. */
export const readColumnPrefs = (tab) => ({ hidden: readSet('hidden', tab), shown: readSet('shown', tab) });

export function writeColumnPrefs(tab, { hidden, shown }) {
    try {
        window.localStorage.setItem(storageKey('hidden', tab), JSON.stringify([...hidden]));
        window.localStorage.setItem(storageKey('shown', tab), JSON.stringify([...shown]));
    } catch { /* storage unavailable */ }
}

/** Switch one column on or off. The record-name column cannot be switched off. */
export function toggleColumn(prefs, column, on) {
    const hidden = new Set(prefs.hidden);
    const shown = new Set(prefs.shown);
    if (on) { shown.add(column.id); hidden.delete(column.id); } else if (!column.primary) { hidden.add(column.id); shown.delete(column.id); }
    return { hidden, shown };
}

export const showAllColumns = (columns) => ({ hidden: new Set(), shown: new Set(columns.map((c) => c.id)) });
export const clearColumns = (columns) => ({ hidden: new Set(columns.filter((c) => !c.primary).map((c) => c.id)), shown: new Set() });
export const resetColumns = () => ({ hidden: new Set(), shown: new Set() });

/**
 * The row states a bulk action taken from each tab may reach — what that tab
 * shows as selectable. Mirrors `contract.VIEW_ACTION_STATES`: the server
 * refuses anything else as `not_in_view` when a call names its view.
 */
const VIEW_ACTION_STATES = {
    imports: ['imported'],
    staging: ['needs_review', 'ready_for_pool', 'skipped'],
    pool: ['pooled', 'committing', 'retryable_error', 'permanent_error'],
    deleted: ['deleted'],
};

export function actionableIn(state, tab) {
    return (VIEW_ACTION_STATES[tab] || []).includes(state);
}

/**
 * After a bulk result, the ids that left this tab (#770 QA): rows the action
 * moved somewhere this tab cannot act on, and rows the server refused as no
 * longer here. They must not stay selected, or the next action — a Delete in
 * Staging — would reach rows the user can no longer see.
 */
export function idsThatLeft(results, tab) {
    return new Set((results || [])
        .filter((r) => (r.ok ? !actionableIn(r.state, tab) : r.error?.code === 'not_in_view'))
        .map((r) => r.row_id));
}

/**
 * Whether a row needs someone's attention, for the coloured left edge:
 * `review` (blocked in Staging, or pooled with no owner yet) or `error`
 * (a commit failed). Imports and Deleted rows are never flagged.
 */
export function attentionOf(row, tab) {
    if (tab !== 'staging' && tab !== 'pool') return null;
    if (row.state === 'retryable_error' || row.state === 'permanent_error') return 'error';
    if (row.state === 'needs_review') return 'review';
    if (row.state === 'pooled' && !row.owner_id) return 'review';
    return null;
}

// ── status vocabulary ─────────────────────────────────────────────────────────

const GOOD = { tone: 'good' };
const WARN = { tone: 'warn' };
const BAD = { tone: 'bad' };
const NEUTRAL = { tone: 'neutral' };
const ACCENT = { tone: 'accent' };
const INFO = { tone: 'info' };

export function statusOf(row, tab) {
    const s = row.state;
    if (tab === 'imports') {
        return ({
            imported: ['New', ACCENT],
            needs_review: ['In staging', NEUTRAL], ready_for_pool: ['In staging', NEUTRAL],
            pooled: ['In pool', NEUTRAL], committing: ['In pool', NEUTRAL],
            retryable_error: ['In pool', NEUTRAL], permanent_error: ['In pool', NEUTRAL],
            committed: ['Committed', GOOD], skipped: ['Skipped', NEUTRAL], reverted: ['Reverted', NEUTRAL],
        })[s] || [s, NEUTRAL];
    }
    if (s === 'needs_review') return ['Needs review', WARN];
    if (s === 'skipped') return ['Skipped (duplicate)', NEUTRAL];
    if (s === 'ready_for_pool') return ['Ready for pool', GOOD];
    if (s === 'pooled') return row.owner_id ? ['In pool', GOOD] : ['Awaiting owner', WARN];
    if (s === 'committing') return ['Committing', INFO];
    if (s === 'retryable_error') return ['Retry needed', WARN];
    if (s === 'permanent_error') return ['Failed', BAD];
    if (s === 'committed') return ['Committed', GOOD];
    if (s === 'deleted') return ['Deleted', NEUTRAL];
    return [s, NEUTRAL];
}

/**
 * Import batch status (#771 state matrix, #775 QA). The server derives it
 * (`batch_status`, services/list_management/imports.py) so the badge and the
 * Import History filter can never disagree; this is only its vocabulary.
 */
export const BATCH_STATUS_META = {
    imported: ['Imported', ACCENT],
    in_staging: ['In staging', NEUTRAL],
    partially_pooled: ['Partially pooled', INFO],
    in_pooling: ['In Pooling', INFO],
    committing: ['Committing', INFO],
    partially_committed: ['Partially committed', WARN],
    committed: ['Committed', GOOD],
    needs_retry: ['Needs retry', BAD],
    reverted: ['Reverted', NEUTRAL],
    reverted_with_conflicts: ['Reverted (conflicts)', WARN],
    deleted: ['Deleted', NEUTRAL],
};
export const BATCH_STATUSES = Object.keys(BATCH_STATUS_META);

export function batchStatusOf(imp) {
    return BATCH_STATUS_META[imp?.batch_status] || BATCH_STATUS_META.imported;
}

/**
 * The Deleted tab's columns. Rows a Delete import removed are marked, so they
 * read differently from rows deleted one by one (#775 QA); `Batch` names the
 * import each row came from. Each carries its tooltip sentence (#975).
 */
export const deletedColumns = (batches) => [
    { id: 'name', label: 'Name', description: 'The record\'s name from the import; select it to open the row.',
      get: (r) => displayName(r), primary: true },
    { id: 'type', label: 'Type', description: 'Whether the row would have become a Company or a Contact in the CRM.',
      get: (r) => r.entity_type || '—' },
    { id: 'company', label: 'Company', description: 'The contact\'s company name from the import.',
      get: (r) => (r.entity_type === 'Contact' ? r.values?.company_name : '—') || '—' },
    { id: 'batch', label: 'Batch', description: 'The import this row came from.',
      get: (r) => batches?.[r.import_id]?.batch_name || '—' },
    { id: 'reason', label: 'Reason', description: 'Why the row was deleted; "With import" means its whole import was deleted.',
      get: (r) => (r.deleted?.via_import ? `With import · ${r.deleted?.reason || '—'}` : r.deleted?.reason || '—') },
    { id: 'by', label: 'Deleted By', description: 'The admin who deleted the row.', get: (r) => r.deleted?.by_name || '—' },
    { id: 'on', label: 'Deleted On', description: 'When the row was deleted.',
      get: (r) => formatDate(r.deleted?.at), sort: 'deleted_at' },
    { id: 'purge', label: 'Purges On', description: 'When the retention policy purges this row; "Kept" means no policy is set, so it is never purged.',
      get: (r) => (r.purge_at ? formatDate(r.purge_at) : 'Kept') },
];

// ── badge and column descriptions (#975) ─────────────────────────────────────
//
// One sentence each: what the state means, then what happens next or what to
// do. They restate the behaviour documented in
// docs/runtime/current/list-management.md (Row states, Batch status, Match
// ladder, Commit/retry/revert, the owner migration) — when a gate there
// changes, change the sentence here with it. Nothing user-identifying goes in
// a tooltip: no names, emails or companies, only codes and counts.

/** Row-state sentences, keyed by the label `statusOf` returns, per tab group: the Imports tab's
 *  "New" and the Match badge's "New" never share a sentence. The other tabs share one table. */
export const STATUS_HELP = {
    other: {
        'Needs review': 'A match or validation problem holds this row in Staging until an admin resolves it.',
        'Ready for pool': 'This row passes every check; Move to Pool queues it for an owner and Commit.',
        'Skipped (duplicate)': 'Its match decision is Skip, so it stays out of the pool and is never written to the CRM.',
        'In pool': 'This row is in the pre-CRM pool with an owner assigned and is written to the CRM when an admin commits.',
        'Awaiting owner': 'This row is in the pool without an owner; Commit blocks it until one is assigned.',
        Committing: 'A commit job is writing this row to the CRM now.',
        'Retry needed': 'The commit hit a temporary error; retry it from Import History or the Commit dialog.',
        Failed: 'The commit failed and will not succeed as it stands; Return to Staging to fix it.',
        Committed: 'This row is in the CRM; reverting its commit in Import History undoes it.',
        Deleted: 'This row was removed before Commit, with a reason, and can be restored from the Deleted tab.',
    },
    imports: {
        New: 'Imported and still read-only here; Move to Staging to review it.',
        'In staging': 'This row has moved on to the Staging Area.',
        'In pool': 'This row has moved on to For Pooling.',
        Committed: 'This row is in the CRM.',
        Skipped: 'Its match decision was Skip, so it was never written to the CRM.',
        Reverted: 'This row was committed and the commit was then reverted.',
    },
};

/** The sentence for a row's status badge: `[label] = statusOf(row, tab)`. */
export function statusHelp(row, tab) {
    const [label] = statusOf(row, tab);
    const table = tab === 'imports' ? STATUS_HELP.imports : STATUS_HELP.other;
    return table[label] || `Row state: ${String(label).replace(/_/g, ' ')}.`;
}

/** Match-state sentences, keyed like `MATCH_LABELS` (`none` = no status yet). */
export const MATCH_HELP = {
    new: 'No existing CRM record matched, so Commit creates a new one.',
    matched_by_source_id: 'Matched an existing record by the source\'s own ID (a PitchBook ID, or the contact ID in an owner migration).',
    matched_by_email: 'Matched an existing contact by exact email.',
    matched_by_domain: 'Matched an existing company by its website or company-email domain.',
    matched_by_name: 'Matched an existing contact by first and last name on the matched company.',
    possible: 'A likely match by name only; review it to link, create new or skip.',
    ambiguous: 'Several records match or the evidence conflicts, so it is held for an admin decision: link one, create new or skip.',
    none: 'Not matched yet.',
};

/** What the " · Skip" / " · Update" suffix on a Match badge means. */
export const DECISION_HELP = {
    skip: 'Decision: Skip, so this row will not be imported.',
    update: 'Decision: Update, so Commit updates the matched record under the write policy.',
};

/** Match evidence codes (`row.match.evidence`, services/list_management/matching.py), in words. */
export const EVIDENCE_LABELS = {
    pitchbook_id: 'PitchBook ID',
    pitchbook_person_id: 'PitchBook person ID',
    email: 'exact email',
    domain: 'website domain',
    name: 'name and company',
    name_and_company: 'name and company',
    name_only: 'name only',
    multiple_pitchbook_matches: 'several PitchBook matches',
    multiple_email_matches: 'several email matches',
    multiple_domain_matches: 'several domain matches',
    multiple_name_matches: 'several name matches',
    domain_conflict: 'conflicting domains',
    duplicate_in_import: 'a duplicate in this import',
    several_companies_in_import: 'several companies in this import',
    contact_id: 'contact ID',
};

/** The Match tooltip's description and detail lines (decision, then evidence). */
export function matchHelp(row) {
    const st = row.match?.status;
    const description = MATCH_HELP[st] || (st ? `Match state: ${String(st).replace(/_/g, ' ')}.` : MATCH_HELP.none);
    const detail = [];
    if (row.match_decision === 'skip') detail.push(DECISION_HELP.skip);
    else if (row.match_decision === 'update') detail.push(DECISION_HELP.update);
    if (row.match?.evidence) detail.push(`Evidence: ${EVIDENCE_LABELS[row.match.evidence] || String(row.match.evidence).replace(/_/g, ' ')}`);
    return { description, detail };
}

/** The Issues badge's description; its detail lines are the row's own validation messages. */
export const ISSUES_HELP = 'Problems that block this row until they are fixed.';

/** Import batch statuses (Import History), keyed like `BATCH_STATUS_META`. */
export const BATCH_HELP = {
    imported: 'No row has left Imports yet.',
    in_staging: 'Rows are in Staging and none has reached For Pooling.',
    partially_pooled: 'Some rows are in For Pooling and others are still in Staging or Imports.',
    in_pooling: 'Every active row is in For Pooling, waiting for Commit.',
    committing: 'A commit job for this import is queued or running.',
    partially_committed: 'Some rows are in the CRM and others are still before Commit.',
    committed: 'Every active row is in the CRM.',
    needs_retry: 'A row hit a commit error; open the import to retry it.',
    reverted: 'The latest commit was reverted.',
    reverted_with_conflicts: 'The latest commit was reverted, except records changed since, which were kept and reported as conflicts.',
    deleted: 'This import was deleted.',
};

/** Commit-job statuses (contract.py `JOB_*`), keyed by the raw job status. */
export const JOB_HELP = {
    queued: 'Waiting to start; it runs in the background, one commit at a time per organisation.',
    running: 'Writing this import\'s rows to the CRM now.',
    completed: 'Finished: every row in it was written to the CRM.',
    partial: 'Finished with some rows in the CRM and others failed or sent back to Staging.',
    failed: 'Finished without writing any row to the CRM; the failed rows say why.',
    cancelled: 'Stopped before it finished; rows it had not started went back to For Pooling.',
    interrupted: 'Stopped part-way (for example by a deploy); Resume finishes the rows it did not.',
    reverting: 'Undoing this commit\'s writes; it runs in the background.',
    reverted: 'This commit\'s writes were undone.',
    reverted_with_conflicts: 'This commit\'s writes were undone, except records changed since, which were kept and reported as conflicts.',
};

/** The one-time owner-migration operation statuses (#862), moved here from LmHistoryView so the
 *  labels and their descriptions sit together. */
export const OPERATION_STATUS = {
    previewed: 'Preparing parts', pending_approval: 'Waiting for approval', approved: 'Approved',
    committing: 'Committing parts', committed: 'Committed', partially_committed: 'Partially committed',
    rejected: 'Rejected', expired: 'Approval expired', restoring: 'Rolling back', restored: 'Rolled back',
    partially_restored: 'Partly rolled back',
};

/** Operation-status sentences, keyed like `OPERATION_STATUS`. */
export const OPERATION_HELP = {
    previewed: 'The parts are being staged; nothing changes until a different admin approves.',
    pending_approval: 'Waiting for a different admin to approve or reject it; the request lapses after 7 days.',
    approved: 'Approved; its parts commit one at a time, in order.',
    committing: 'Its parts are committing one at a time, in order.',
    committed: 'Every part is in the CRM; Roll back undoes them.',
    partially_committed: 'Some parts are in the CRM and others are not yet.',
    rejected: 'A different admin rejected it, so no further part runs.',
    expired: 'No admin decided within 7 days, so the request lapsed and no further part runs.',
    restoring: 'Rolling the migration back, newest part first.',
    restored: 'Every contact was given back its previous owner.',
    partially_restored: 'The rollback finished with contacts kept as conflicts.',
};

/** The header and drawer badges that are not row states, keyed by their tag. */
export const TAG_HELP = {
    owner_migration: 'A one-time Contact Owner migration: it changes contact owners and nothing else.',
    migration_part: 'One part of a Contact Owner migration; each part commits and rolls back on its own.',
    custom: 'A custom field, defined in Admin → Custom Fields or while importing.',
    file: FILE_COLUMN_DESCRIPTION,
    owner: 'The owner this row is assigned to; Commit needs one.',
    no_company: 'A non-blocking warning: this contact has no company.',
};

export const MATCH_LABELS = {
    new: 'New',
    matched_by_source_id: 'PitchBook ID',
    matched_by_email: 'Email match',
    matched_by_domain: 'Domain match',
    matched_by_name: 'Name + company',
    possible: 'Possible match',
    ambiguous: 'Ambiguous',
};

export function matchOf(row) {
    const st = row.match?.status;
    if (!st) return ['—', NEUTRAL];
    const label = MATCH_LABELS[st] || st;
    const decision = row.match_decision;
    const suffix = decision === 'skip' ? ' · Skip' : decision === 'update' && st !== 'new' ? ' · Update' : '';
    if (st === 'ambiguous') return [label + suffix, BAD];
    if (st === 'possible') return [label + suffix, WARN];
    if (st === 'new') return [label, ACCENT];
    return [label + suffix, GOOD];
}

export const SOURCE_META = {
    pitchbook: { label: 'PitchBook', icon: 'Building2', tone: 'accent' },
    csv: { label: 'CSVs / Sheet', icon: 'FileText', tone: 'warn' },
    legacy_staging: { label: 'Legacy staging', icon: 'History', tone: 'neutral' },
    all: { label: 'All Imports', icon: 'Layers', tone: 'neutral' },
};

export const BLOCK_REASONS = {
    owner_missing: 'Missing owner',
    owner_inactive: 'Owner no longer active',
    not_found: 'Not found',
    match_unresolved: 'Unresolved match',
    company_match_unresolved: 'Unresolved company match',
    company_link_required: 'Contact needs a company',
    company_needs_contact: 'Company needs a contact',
    relationship_endpoint_unresolved: 'Unresolved relationship',
    relationship_type_invalid: 'Invalid relationship type',
    email_invalid: 'Invalid email',
    stale_transform: 'Import rules changed',
    transform_updated: 'Values re-read, review again',
    stale_source: 'Source changed',
    import_unavailable: 'Import deleted',
    update_target_missing: 'No update target',
    lineage_missing: 'Missing source lineage',
    entity_type_unresolved: 'No name or company',
    source_approval_inactive: 'Source licensing approval inactive',
    // The one-time Contact Owner migration (#862).
    contact_not_found: 'No matching contact',
    contact_deleted: 'Contact deleted',
    contact_ambiguous: 'Several contacts share this email',
    duplicate_contact_in_file: 'Same contact twice in the file',
    duplicate_contact_across_parts: 'Same contact in another part',
    duplicate_source_row: 'Same email twice in the file',
    owner_unresolved: 'Owner not found',
    owner_ambiguous: 'Owner name matches several people',
};

/**
 * Blocked commit reasons a For Pooling table filter can show in full (#773):
 * the URL-state patch, and how the link names it. Reasons without a matching
 * filter link to For Pooling unfiltered.
 */
export const BLOCK_POOL_FILTERS = {
    owner_missing: { patch: { owner: 'unassigned' }, label: 'Owner: Unassigned' },
};

export function blockLabel(code) {
    if (code?.startsWith('state_')) return `Not in pool (${code.slice(6).replace(/_/g, ' ')})`;
    return BLOCK_REASONS[code] || (code || '').replace(/_/g, ' ');
}

export function displayName(row) {
    const val = row.values || row.staged_values || {};
    const n = `${val.first_name || ''} ${val.last_name || ''}`.trim();
    return n || val.company_name || row.row_id;
}

export function formatDate(iso, withTime = false) {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    const opts = withTime
        ? { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }
        : { day: 'numeric', month: 'short', year: 'numeric' };
    return d.toLocaleString(undefined, opts);
}
